var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
import { inject, ref, watch, nextTick, defineComponent, onMounted, computed, resolveComponent, unref, openBlock, createElementBlock, createElementVNode, createBlock, withCtx, createTextVNode, toDisplayString, createCommentVNode, withModifiers, Fragment, normalizeClass, renderSlot, renderList, withDirectives, vModelCheckbox } from "vue";
import { routeKey, loadingProgressKey, meta } from "@drapejs/core";
import consola from "consola";
function usePageView(enricher) {
  const route = inject(routeKey, {});
  const loadingProgress = inject(loadingProgressKey, ref(null));
  watch(loadingProgress, () => {
    if (typeof loadingProgress.value === "number") {
      return;
    }
    nextTick(async () => {
      var _a;
      if (!hasDataLayer() || !route.pathname) {
        return;
      }
      if (!enricher) {
        enricher = () => Promise.resolve({});
      }
      const pageLocation = `${stripTrailingSlash(route.pathname)}${route.search ? `?${route.search}` : ""}`;
      consola.trace("GTM: page_view");
      window.dataLayer.push(__spreadValues({
        event: "page_view",
        page_location: pageLocation,
        page_title: (_a = meta.value) == null ? void 0 : _a.title,
        page_component: route.component,
        ecommerce: null
      }, await enricher()));
    });
  });
  function stripTrailingSlash(url) {
    return url.endsWith("/") ? url.slice(0, -1) : url;
  }
}
function useConsent() {
  const consent = ref(getSavedConsent());
  watch(consent, () => {
    if (hasDataLayer()) {
      consola.trace("GTM: set_consent");
      if (!consent.value) {
        return;
      }
      let payload;
      if (typeof consent.value.types.___legacy === "boolean") {
        payload = {
          event: "set_consent",
          consent: consent.value.types.___legacy ? "granted" : "denied"
        };
      } else {
        payload = Object.keys(consent.value.types).reduce((res, c) => {
          var _a;
          res.consent[c] = ((_a = consent.value) == null ? void 0 : _a.types[c]) ? "granted" : "denied";
          return res;
        }, {
          event: "set_consent",
          consent: {}
        });
      }
      window.dataLayer.push(payload);
    }
  }, {
    deep: true
  });
  return {
    saveConsent(value) {
      consent.value = {
        timestamp: new Date().toUTCString(),
        types: value
      };
      window.localStorage.setItem("consent", JSON.stringify(consent.value));
    },
    consent
  };
  function getSavedConsent() {
    if (hasStorage()) {
      const storageData = localStorage.getItem("consent");
      if (storageData) {
        return JSON.parse(storageData);
      }
    }
    return null;
  }
}
function useCookieConsent() {
  const consent = ref(getState());
  setDataLayerConsent(consent.value === true ? true : false);
  return {
    consent,
    giveConsent() {
      consent.value = true;
      if (hasCookies()) {
        document.cookie = `consent=true|${Date.now()};max-age=31536000`, setDataLayerConsent(true);
      }
    },
    rejectConsent() {
      consent.value = false;
      if (hasCookies()) {
        document.cookie = "consent=false";
        setDataLayerConsent(false);
      }
    }
  };
  function getState() {
    if (hasCookies()) {
      if (document.cookie.indexOf("consent=true") >= 0) {
        return true;
      }
      if (document.cookie.indexOf("consent=false") >= 0) {
        return false;
      }
    }
    return null;
  }
}
let lastConsentPushed;
function setDataLayerConsent(value) {
  if (hasDataLayer()) {
    if (value === lastConsentPushed) {
      return;
    }
    consola.trace("GTM: set_consent");
    lastConsentPushed = value;
    window.dataLayer.push({
      event: "set_consent",
      consent: value ? "granted" : "denied"
    });
  }
}
function useGA4(options) {
  let searchDebounce;
  let lastSearchTerm;
  let lastItemView;
  let lastItemSelected;
  const itemEnricher = (options == null ? void 0 : options.itemEnricher) || ((item) => item);
  return {
    search(searchTerm, customVariables) {
      if (hasDataLayer()) {
        if (!searchTerm || searchTerm === lastSearchTerm) {
          return;
        }
        lastSearchTerm = searchTerm;
        if (searchDebounce) {
          clearTimeout(searchDebounce);
        }
        searchDebounce = setTimeout(() => {
          consola.trace("GTM: search");
          window.dataLayer.push(__spreadValues({
            event: "search",
            search_term: searchTerm
          }, customVariables));
        }, 500);
      }
    },
    addToCart(currency, items, customVariables) {
      var _a;
      if (hasDataLayer()) {
        consola.trace("GTM: add_to_cart");
        const value = items.map((item) => (item.quantity || 0) * ((item.price || 0) - (item.discount || 0))).reduce((res, i) => res + i, 0);
        const payload = __spreadValues({
          event: "add_to_cart",
          currency,
          value,
          items: (_a = fixGA4Items(items)) == null ? void 0 : _a.map(itemEnricher)
        }, customVariables);
        if (options == null ? void 0 : options.useLegacyEnhancedECommerce) {
          cleanEcommerce();
          payload.ecommerce = {
            currencyCode: currency,
            add: {
              products: items.map((item) => {
                return {
                  name: item.item_name,
                  id: item.item_id,
                  price: getUAItemPrice(item),
                  brand: item.item_brand,
                  category: [item.item_category, item.item_category2, item.item_category3, item.item_category4, item.item_category5].filter(Boolean).join(" / "),
                  variant: item.item_variant,
                  quantity: item.quantity
                };
              })
            }
          };
        }
        window.dataLayer.push(payload);
      }
    },
    removeFromCart(currency, items, customVariables) {
      var _a;
      if (hasDataLayer()) {
        consola.trace("GTM: remove_from_cart");
        const value = items.map((item) => (item.quantity || 0) * ((item.price || 0) - (item.discount || 0))).reduce((res, i) => res + i, 0);
        const payload = __spreadValues({
          event: "remove_from_cart",
          currency,
          value,
          items: (_a = fixGA4Items(items)) == null ? void 0 : _a.map(itemEnricher)
        }, customVariables);
        if (options == null ? void 0 : options.useLegacyEnhancedECommerce) {
          cleanEcommerce();
          payload.ecommerce = {
            currencyCode: currency,
            remove: {
              products: items.map((item) => {
                return {
                  name: item.item_name,
                  id: item.item_id,
                  price: getUAItemPrice(item),
                  brand: item.item_brand,
                  category: [item.item_category, item.item_category2, item.item_category3, item.item_category4, item.item_category5].filter(Boolean).join(" / "),
                  variant: item.item_variant,
                  quantity: item.quantity
                };
              })
            }
          };
        }
        window.dataLayer.push(payload);
      }
    },
    beginCheckout(data) {
      var _a, _b;
      if (!hasDataLayer()) {
        return;
      }
      consola.trace("GTM: begin_checkout");
      if (!data) {
        const payload2 = {
          event: "begin_checkout"
        };
        if (options == null ? void 0 : options.useLegacyEnhancedECommerce) {
          cleanEcommerce();
          payload2.ecommerce = {
            checkout: {
              actionField: { step: 1 }
            }
          };
        }
        window.dataLayer.push(payload2);
        return;
      }
      const value = (((_a = data.items) == null ? void 0 : _a.map((item) => (item.quantity || 0) * ((item.price || 0) - (item.discount || 0))).reduce((res, i) => res + i, 0)) || 0) - (data.orderDiscount || 0);
      const payload = __spreadValues({
        event: "begin_checkout",
        currency: data.currency,
        value,
        coupon: data.coupon,
        items: (_b = fixGA4Items(data.items)) == null ? void 0 : _b.map(itemEnricher)
      }, data.customVariables);
      if (options == null ? void 0 : options.useLegacyEnhancedECommerce) {
        cleanEcommerce();
        payload.ecommerce = {
          currencyCode: data.currency,
          checkout: {
            actionField: { step: 1 }
          }
        };
        if (data.items) {
          payload.ecommerce.checkout.products = data.items.map((item) => {
            return {
              name: item.item_name,
              id: item.item_id,
              price: getUAItemPrice(item),
              brand: item.item_brand,
              category: [item.item_category, item.item_category2, item.item_category3, item.item_category4, item.item_category5].filter(Boolean).join(" / "),
              variant: item.item_variant,
              quantity: item.quantity
            };
          });
        }
      }
      window.dataLayer.push(payload);
    },
    purchase(data) {
      var _a, _b, _c, _d;
      if (!data) {
        return;
      }
      if (hasDataLayer()) {
        consola.trace("GTM: purchase");
        const value = (((_a = data.items) == null ? void 0 : _a.map((item) => (item.quantity || 1) * ((item.price || 0) - (item.discount || 0))).reduce((res, i) => res + i, 0)) || 0) - (data.orderDiscount || 0);
        const payload = __spreadValues({
          event: "purchase",
          transaction_id: data.transactionId,
          currency: data.currency,
          tax: data.tax,
          shipping: data.shipping,
          affiliation: data.affiliation,
          value,
          coupon: data.coupon,
          items: (_b = fixGA4Items(data.items)) == null ? void 0 : _b.map(itemEnricher)
        }, data.customVariables);
        if (options == null ? void 0 : options.useLegacyEnhancedECommerce) {
          cleanEcommerce();
          payload.ecommerce = {
            currencyCode: data.currency,
            purchase: {
              actionField: {
                id: data.transactionId,
                affiliation: data.affiliation,
                revenue: value == null ? void 0 : value.toString(),
                tax: (_c = data.tax) == null ? void 0 : _c.toString(),
                shipping: (_d = data.shipping) == null ? void 0 : _d.toString(),
                coupon: data.coupon
              }
            }
          };
          if (data.items) {
            payload.ecommerce.purchase.products = data.items.map((item) => {
              return {
                name: item.item_name,
                id: item.item_id,
                price: getUAItemPrice(item),
                brand: item.item_brand,
                category: [item.item_category, item.item_category2, item.item_category3, item.item_category4, item.item_category5].filter(Boolean).join(" / "),
                variant: item.item_variant,
                quantity: item.quantity
              };
            });
          }
        }
        window.dataLayer.push(payload);
      }
    },
    viewItem(currency, item, customVariables) {
      var _a;
      if (hasDataLayer()) {
        if (item.item_id && item.item_id === lastItemView) {
          return;
        }
        lastItemView = item.item_id;
        consola.trace("GTM: view_item");
        const value = (item.price || 0) - (item.discount || 0);
        const payload = __spreadValues({
          event: "view_item",
          currency,
          value,
          items: (_a = fixGA4Items([item])) == null ? void 0 : _a.map(itemEnricher)
        }, customVariables);
        if (options == null ? void 0 : options.useLegacyEnhancedECommerce) {
          cleanEcommerce();
          payload.ecommerce = {
            detail: {
              products: [{
                name: item.item_name,
                id: item.item_id,
                price: getUAItemPrice(item),
                brand: item.item_brand,
                category: [item.item_category, item.item_category2, item.item_category3, item.item_category4, item.item_category5].filter(Boolean).join(" / "),
                varaint: item.item_variant
              }]
            }
          };
        }
        window.dataLayer.push(payload);
      }
    },
    selectItem(data) {
      var _a;
      if (!data || !data.item) {
        return;
      }
      const item = data.item;
      if (hasDataLayer()) {
        if (item.item_id && item.item_id === lastItemSelected) {
          return;
        }
        lastItemSelected = data.item.item_id;
        consola.trace("GTM: select_item");
        const payload = __spreadValues({
          event: "select_item",
          item_list_id: data.itemListId,
          item_list_name: data.itemListName,
          items: (_a = fixGA4Items([data.item])) == null ? void 0 : _a.map(itemEnricher)
        }, data.customVariables);
        if (options == null ? void 0 : options.useLegacyEnhancedECommerce) {
          cleanEcommerce();
          payload.ecommerce = {
            click: {
              actionField: {
                list: data.itemListName
              },
              products: [{
                name: item.item_name,
                id: item.item_id,
                price: getUAItemPrice(item),
                brand: item.item_brand,
                category: [item.item_category, item.item_category2, item.item_category3, item.item_category4, item.item_category5].filter(Boolean).join(" / "),
                varaint: item.item_variant,
                position: item.index
              }]
            }
          };
        }
        window.dataLayer.push(payload);
      }
    }
  };
  function cleanEcommerce() {
    if (options == null ? void 0 : options.useLegacyEnhancedECommerce) {
      window.dataLayer.push({
        ecommerce: null
      });
    }
  }
}
function hasStorage() {
  return typeof document !== "undefined";
}
function hasCookies() {
  return typeof document !== "undefined";
}
function hasDataLayer() {
  return typeof window !== "undefined" && typeof window.dataLayer !== "undefined";
}
function fixGA4Items(items) {
  if (!items)
    return;
  return items.map(fixGA4Item);
}
function fixGA4Item(item) {
  return __spreadProps(__spreadValues({}, item), {
    price: getUAItemPrice(item),
    discount: void 0
  });
}
function getUAItemPrice(item) {
  return Math.max((item.price || 0) - (item.discount || 0), 0);
}
var TrackingConsent_vue_vue_type_style_index_0_lang = "";
const _hoisted_1$1 = {
  key: 0,
  class: "tracking-consent"
};
const _hoisted_2$1 = ["innerHTML"];
const _hoisted_3$1 = { class: "tracking-consent__actions" };
const _sfc_main$1 = /* @__PURE__ */ defineComponent({
  name: "TrackingConsent",
  props: {
    message: null,
    readMoreUrl: null,
    readMoreLabel: null,
    grantLabel: null,
    denyLabel: null
  },
  setup(__props) {
    const { consent, saveConsent } = useConsent();
    const isMounted = ref(false);
    onMounted(() => {
      isMounted.value = true;
    });
    const show = computed(() => isMounted.value && consent.value === null);
    function giveConsent() {
      saveConsent({
        ___legacy: true
      });
    }
    function rejectConsent() {
      saveConsent({
        ___legacy: false
      });
    }
    return (_ctx, _cache) => {
      const _component_router_link = resolveComponent("router-link");
      return unref(show) ? (openBlock(), createElementBlock("div", _hoisted_1$1, [
        createElementVNode("span", {
          class: "tracking-consent__message",
          innerHTML: __props.message
        }, null, 8, _hoisted_2$1),
        __props.readMoreUrl ? (openBlock(), createBlock(_component_router_link, {
          key: 0,
          to: __props.readMoreUrl,
          class: "tracking-consent__read-more"
        }, {
          default: withCtx(() => [
            createTextVNode(toDisplayString(__props.readMoreLabel), 1)
          ]),
          _: 1
        }, 8, ["to"])) : createCommentVNode("", true),
        createElementVNode("div", _hoisted_3$1, [
          createElementVNode("a", {
            href: "#",
            onClick: _cache[0] || (_cache[0] = withModifiers(($event) => giveConsent(), ["prevent"])),
            class: "tracking-consent__grant primary-button"
          }, toDisplayString(__props.grantLabel), 1),
          createElementVNode("a", {
            href: "#",
            onClick: _cache[1] || (_cache[1] = withModifiers(($event) => rejectConsent(), ["prevent"])),
            class: "tracking-consent__deny secondary-button"
          }, toDisplayString(__props.denyLabel), 1)
        ])
      ])) : createCommentVNode("", true);
    };
  }
});
var TrackingConsentV2_vue_vue_type_style_index_0_lang = "";
const _hoisted_1 = { class: "tracking-consent-v2__margin" };
const _hoisted_2 = { class: "tracking-consent-v2__description" };
const _hoisted_3 = { class: "tracking-consent-v2__services" };
const _hoisted_4 = { class: "tracking-consent-v2__service tracking-consent-v2__service--essentials" };
const _hoisted_5 = /* @__PURE__ */ createElementVNode("input", {
  type: "checkbox",
  checked: "true",
  disabled: "true"
}, null, -1);
const _hoisted_6 = ["onUpdate:modelValue"];
const _hoisted_7 = { class: "tracking-consent-v2__submit" };
const _sfc_main = /* @__PURE__ */ defineComponent({
  name: "TrackingConsentV2",
  props: {
    privacyPolicyUrl: null,
    consents: null,
    essentialsLabel: null,
    grantAllLabel: null,
    saveLabel: null
  },
  setup(__props) {
    const props = __props;
    const isMounted = ref(false);
    const { saveConsent, consent } = useConsent();
    const route = inject(routeKey, {});
    onMounted(() => {
      isMounted.value = true;
    });
    const consentViewModel = ref(props.consents.map((c) => {
      var _a;
      return {
        type: c.type,
        label: c.label,
        value: ((_a = consent.value) == null ? void 0 : _a.types[c.type]) || false
      };
    }));
    const show = computed(() => isMounted.value && consent.value === null && !route.pathname.startsWith(props.privacyPolicyUrl));
    const showClass = ref(false);
    function grantAllConsent() {
      saveConsent(consentViewModel.value.reduce((res, c) => {
        res[c.type] = true;
        return res;
      }, {}));
    }
    function updateConsent() {
      saveConsent(consentViewModel.value.reduce((res, c) => {
        res[c.type] = c.value;
        return res;
      }, {}));
    }
    watch(show, (val) => {
      setTimeout(() => {
        showClass.value = val;
      }, 0);
    }, { immediate: true });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock(Fragment, null, [
        unref(show) ? (openBlock(), createElementBlock("div", {
          key: 0,
          class: normalizeClass(["tracking-consent-v2__modal", { "tracking-consent-v2__modal--shown": showClass.value }])
        }, null, 2)) : createCommentVNode("", true),
        unref(show) ? (openBlock(), createElementBlock("div", {
          key: 1,
          class: normalizeClass(["tracking-consent-v2", { "tracking-consent-v2--shown": showClass.value }])
        }, [
          createElementVNode("div", _hoisted_1, [
            createElementVNode("div", _hoisted_2, [
              renderSlot(_ctx.$slots, "default")
            ]),
            createElementVNode("div", _hoisted_3, [
              createElementVNode("label", _hoisted_4, [
                _hoisted_5,
                createTextVNode(" " + toDisplayString(__props.essentialsLabel), 1)
              ]),
              (openBlock(true), createElementBlock(Fragment, null, renderList(consentViewModel.value, (consent2) => {
                return openBlock(), createElementBlock("label", {
                  key: consent2.type,
                  class: "tracking-consent-v2__service"
                }, [
                  withDirectives(createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": ($event) => consent2.value = $event
                  }, null, 8, _hoisted_6), [
                    [vModelCheckbox, consent2.value]
                  ]),
                  createTextVNode(" " + toDisplayString(consent2.label), 1)
                ]);
              }), 128))
            ]),
            createElementVNode("div", _hoisted_7, [
              createElementVNode("a", {
                href: "#",
                onClick: _cache[0] || (_cache[0] = withModifiers(($event) => grantAllConsent(), ["prevent"])),
                class: "tracking-consent-v2__grant-all button-primary"
              }, toDisplayString(__props.grantAllLabel), 1),
              createElementVNode("a", {
                href: "#",
                onClick: _cache[1] || (_cache[1] = withModifiers(($event) => updateConsent(), ["prevent"])),
                class: "tracking-consent-v2__save button-secondary"
              }, toDisplayString(__props.saveLabel), 1)
            ])
          ])
        ], 2)) : createCommentVNode("", true)
      ], 64);
    };
  }
});
export { _sfc_main$1 as TrackingConsent, _sfc_main as TrackingConsentV2, useConsent, useCookieConsent, useGA4, usePageView };
